import { useState } from "react";
import { Link } from "react-router-dom";
import { useTimer } from "@/context/TimerContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pause, Play, Square, Clock3 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function fmt(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${sec.toString().padStart(2, "0")}s`;
  return `${sec}s`;
}

export default function FloatingTimer() {
  const { timer, pause, resume, stop, liveWorkedSeconds } = useTimer();
  const [stopOpen, setStopOpen] = useState(false);
  const [stopResult, setStopResult] = useState(null); // {hours, task_id}
  const [moving, setMoving] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const seconds = timer ? liveWorkedSeconds() : 0;
  const paused = !!timer?.is_paused;

  const onStopClick = async () => {
    try {
      const entry = await stop();
      setStopResult(entry);
      setStopOpen(true);
    } catch (e) { console.warn("[FloatingTimer] stop failed", e); /* toast handled in context */ }
  };

  const moveActivity = async (status) => {
    if (!stopResult?.task_id) { setStopOpen(false); return; }
    setMoving(true);
    try {
      await api.put(`/tasks/${stopResult.task_id}`, { status });
      toast.success(`Activity moved to ${status === "review" ? "Review" : status === "done" ? "Done" : status}`);
    } catch (e) { toast.error("Failed to move activity"); }
    setMoving(false);
    setStopOpen(false);
  };

  return (
    <>
      {timer && (
      <div
        className={`fixed bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-2 border rounded-sm shadow-lg ${
          paused ? "bg-amber-50 border-amber-300" : "bg-neutral-900 border-neutral-900 text-white"
        }`}
        data-testid="floating-timer"
      >
        <Clock3 size={14} className={paused ? "text-amber-700" : "text-emerald-300"} />
        <div className="flex flex-col leading-tight">
          <Link
            to={timer.project_id ? `/projects/${timer.project_id}` : "/tasks"}
            className={`text-[10px] uppercase tracking-widest ${paused ? "text-amber-700" : "text-neutral-300"} hover:underline`}
            data-testid="floating-timer-link"
          >
            {paused ? "Paused" : "Timing"}
          </Link>
          <div className={`font-mono text-xs ${paused ? "text-amber-900" : "text-white"}`} data-testid="floating-timer-seconds">
            {fmt(seconds)}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-1">
          {paused ? (
            <Button size="sm" onClick={resume} className="h-7 w-7 p-0 rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="floating-timer-resume" title="Resume">
              <Play size={12} />
            </Button>
          ) : (
            <Button size="sm" onClick={pause} className="h-7 w-7 p-0 rounded-sm bg-neutral-700 hover:bg-neutral-600 text-white" data-testid="floating-timer-pause" title="Pause">
              <Pause size={12} />
            </Button>
          )}
          <Button size="sm" onClick={onStopClick} className="h-7 w-7 p-0 rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="floating-timer-stop" title="Stop">
            <Square size={12} />
          </Button>
        </div>
      </div>
      )}

      <Dialog open={stopOpen} onOpenChange={setStopOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="timer-stopped-dialog">
          <DialogHeader>
            <DialogTitle>Logged {stopResult?.hours ?? 0}h</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-600 mb-3">
            {isAdmin ? "Want to update this activity's status?" : "Your time entry has been saved."}
          </div>
          <DialogFooter className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setStopOpen(false)} disabled={moving} className="rounded-sm" data-testid="timer-stopped-keep">
              {isAdmin ? "Keep in progress" : "Close"}
            </Button>
            {isAdmin && (
              <>
                <Button
                  onClick={() => moveActivity("review")}
                  disabled={moving || !stopResult?.task_id}
                  className="rounded-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-40"
                  data-testid="timer-stopped-review"
                  title={stopResult?.task_id ? "" : "No activity linked to this timer"}
                >
                  Move to Review
                </Button>
                <Button
                  onClick={() => moveActivity("done")}
                  disabled={moving || !stopResult?.task_id}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                  data-testid="timer-stopped-done"
                  title={stopResult?.task_id ? "" : "No activity linked to this timer"}
                >
                  Move to Done
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
